import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { InfiniteApplicationApplicant, getInfiniteApplication } from '~/services/LuxLoyaltyInfiniteApplicationService';
import { formatDateDDMonYY } from '~/services/TimeService';

import FinalizeApplicationDialog from './FinalizeApplicationDialog';
import { ApplicantAccountState, InfiniteApplicationEnrolmentRequest } from './ReviewApplicationApplicantActions';
import { ApplicantsTable, AssociatedEligibilityTable, BookingAuthoritiesTable } from './ReviewApplicationTables';
import { StatusChip, appendApplicationNote } from './infiniteApplicationReview.utils';

export type PendingApplicationAction = {
  action: 'APPROVE' | 'REJECT' | 'REOPEN';
  applicationId: string;
  applicant: InfiniteApplicationApplicant;
  currentNotes?: string | null;
};

export type { InfiniteApplicationEnrolmentRequest };

export default function ReviewApplicationDialog({
  applicationId,
  onClose,
  onApprove,
  onFinalize,
  onStartEnrolment,
  isMutating,
}: {
  applicationId: string | null;
  onClose: () => void;
  onApprove: (action: PendingApplicationAction) => void;
  onFinalize: (
    applicationId: string,
    applicantCustomerMappings: Array<{ applicationApplicantId: string; customerId: string }>,
    notes?: string,
  ) => void;
  onStartEnrolment: (request: InfiniteApplicationEnrolmentRequest) => void;
  isMutating: boolean;
}) {
  const queryClient = useQueryClient();
  const [applicantAccountStates, setApplicantAccountStates] = useState<Record<string, ApplicantAccountState>>({});
  const [isFinalizeConfirmOpen, setIsFinalizeConfirmOpen] = useState(false);
  const [finalizeNotes, setFinalizeNotes] = useState('');
  const applicationQuery = useQuery({
    queryKey: ['lux-loyalty', 'infinite-application', applicationId],
    queryFn: () => getInfiniteApplication(applicationId || ''),
    enabled: Boolean(applicationId),
  });

  const application = applicationQuery.data?.result;
  const isRefreshing = applicationQuery.isFetching;
  const applicants = useMemo(() => application?.applicants ?? [], [application?.applicants]);
  const bookingAuthorities = useMemo(() => application?.bookingAuthorities ?? [], [application?.bookingAuthorities]);
  const associatedEligibility = application?.associatedEligibility ?? null;
  const primaryApplicant = applicants.find((applicant) => applicant.role === 'PRIMARY');
  const applicantCustomerMappings = useMemo(
    () =>
      applicants.flatMap((applicant) => {
        const accountState = applicantAccountStates[applicant.id];
        if (!accountState?.isInfinite || !accountState.customerId) {
          return [];
        }

        return [{ applicationApplicantId: applicant.id, customerId: accountState.customerId }];
      }),
    [applicantAccountStates, applicants],
  );
  const existingProfilesForApplicants = useMemo(
    () =>
      applicants.flatMap((applicant) => {
        const profile = applicantAccountStates[applicant.id]?.infiniteProfile;
        return profile ? [{ applicant, profile }] : [];
      }),
    [applicants, applicantAccountStates],
  );
  const existingBookingAuthoritiesForApplicants = useMemo(
    () =>
      applicants.flatMap((applicant) => {
        const bookingAuthoritiesForProfile =
          applicantAccountStates[applicant.id]?.infiniteProfile?.bookingAuthorities?.filter(
            (authority) => !authority.isDeactivated,
          ) ?? [];

        return bookingAuthoritiesForProfile.length > 0
          ? [{ applicant, bookingAuthorities: bookingAuthoritiesForProfile }]
          : [];
      }),
    [applicants, applicantAccountStates],
  );
  const isCheckingApplicantAccounts =
    application?.status === 'APPROVED' &&
    applicants.some((applicant) => applicantAccountStates[applicant.id]?.isChecking ?? true);
  const canFinalize =
    application?.status === 'APPROVED' &&
    applicants.length > 0 &&
    applicantCustomerMappings.length === applicants.length &&
    !isCheckingApplicantAccounts;

  useEffect(() => {
    setApplicantAccountStates({});
    setFinalizeNotes('');
  }, [applicationId]);

  useEffect(() => {
    if (isFinalizeConfirmOpen) {
      setFinalizeNotes('');
    }
  }, [isFinalizeConfirmOpen]);

  const handleApplicantAccountStateChange = useCallback((applicantId: string, state: ApplicantAccountState) => {
    setApplicantAccountStates((current) => {
      const previous = current[applicantId];
      if (
        previous?.isChecking === state.isChecking &&
        previous?.isInfinite === state.isInfinite &&
        previous?.customerId === state.customerId &&
        previous?.infiniteProfile?.id === state.infiniteProfile?.id
      ) {
        return current;
      }

      return { ...current, [applicantId]: state };
    });
  }, []);

  const refreshApplication = () => {
    if (!applicationId) {
      return;
    }

    void queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-application', applicationId] });
    void queryClient.invalidateQueries({ queryKey: ['users', 'infinite-application-email'] });
    void queryClient.invalidateQueries({ queryKey: ['users', 'infinite-application-account-details'] });
    void queryClient.invalidateQueries({ queryKey: ['loyalty-account'] });
  };

  const confirmFinalize = () => {
    if (!application || !canFinalize) {
      return;
    }

    setIsFinalizeConfirmOpen(false);
    const notes = appendApplicationNote(application.notes, finalizeNotes, 'Finalize note');
    onFinalize(application.id, applicantCustomerMappings, notes);
  };

  return (
    <Dialog open={Boolean(applicationId)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        Review application
        <IconButton
          aria-label="Refresh application"
          onClick={refreshApplication}
          disabled={!applicationId || isRefreshing}
          size="small"
        >
          {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {applicationQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {applicationQuery.error instanceof Error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {applicationQuery.error.message}
          </Alert>
        )}

        {application && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StatusChip status={application.status} />
              <Typography variant="body2" color="text.secondary">
                Submitted {formatDateDDMonYY(application.createdAt)}
              </Typography>
            </Stack>

            {application.notes && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Application note
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {application.notes}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Eligible user
              </Typography>
              <Alert
                severity={associatedEligibility ? 'success' : 'warning'}
                sx={{ mb: associatedEligibility ? 1 : 0 }}
              >
                {associatedEligibility
                  ? 'Found eligible user matching primary applicant.'
                  : 'No eligible user found matching primary applicant.'}
              </Alert>
              {associatedEligibility && <AssociatedEligibilityTable eligibility={associatedEligibility} />}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Applicants
              </Typography>
              <ApplicantsTable
                application={application}
                applicants={applicants}
                applicationStatus={application.status}
                onStartEnrolment={onStartEnrolment}
                onAccountStateChange={handleApplicantAccountStateChange}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Booking authorities
              </Typography>
              <BookingAuthoritiesTable authorities={bookingAuthorities} />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {application?.status === 'SUBMITTED' && (
          <>
            <Button
              color="error"
              disabled={!primaryApplicant || isMutating}
              onClick={() => {
                if (primaryApplicant) {
                  onApprove({
                    action: 'REJECT',
                    applicationId: application.id,
                    applicant: primaryApplicant,
                    currentNotes: application.notes,
                  });
                }
              }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              disabled={!primaryApplicant || isMutating}
              onClick={() => {
                if (primaryApplicant) {
                  onApprove({
                    action: 'APPROVE',
                    applicationId: application.id,
                    applicant: primaryApplicant,
                    currentNotes: application.notes,
                  });
                }
              }}
            >
              Approve
            </Button>
          </>
        )}
        {application?.status === 'REJECTED' && (
          <Button
            variant="contained"
            disabled={!primaryApplicant || isMutating}
            onClick={() => {
              if (primaryApplicant) {
                onApprove({
                  action: 'REOPEN',
                  applicationId: application.id,
                  applicant: primaryApplicant,
                  currentNotes: application.notes,
                });
              }
            }}
          >
            Reopen
          </Button>
        )}
        {application?.status === 'APPROVED' && (
          <Button
            variant="contained"
            color="success"
            disabled={!canFinalize || isMutating}
            onClick={() => setIsFinalizeConfirmOpen(true)}
          >
            {isCheckingApplicantAccounts ? 'Checking applicants...' : 'Finalize'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <FinalizeApplicationDialog
        application={application}
        primaryApplicant={primaryApplicant}
        existingProfilesForApplicants={existingProfilesForApplicants}
        existingBookingAuthoritiesForApplicants={existingBookingAuthoritiesForApplicants}
        notes={finalizeNotes}
        open={isFinalizeConfirmOpen}
        canFinalize={canFinalize}
        isMutating={isMutating}
        onClose={() => setIsFinalizeConfirmOpen(false)}
        onNotesChange={setFinalizeNotes}
        onConfirm={confirmFinalize}
      />
    </Dialog>
  );
}
